import moment from "moment";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { type BTLoginTypes, type HoSubAck, type POVerifyWorkStatuses } from "types/enum";

import { type RfiStatus } from "commonComponents/entity/rfi/RfiContainer/RfiContainer.api.types";

import { type POPaymentStatus } from "entity/purchaseOrderPayment/common/PurchaseOrderPaymentStatus/PurchaseOrderPaymentStatus";

import type { default as InternalUserDeleteConflictsJson } from "./InternalUserDeleteConflicts.api.json";

/** The maximum number of purchase order jobs before requiring
 * the user to mass assign all purchase order jobs to another user. */
export const purchaseOrderJobLimit = 15;

export type InternalUserDeleteConflictsFormActions = undefined | "submitting";

export type InternalUserDeleteConflictsJsonType =
    | typeof InternalUserDeleteConflictsJson.WithJobsToReplace
    | typeof InternalUserDeleteConflictsJson.WithJobsToReplaceAndNoUsers
    | typeof InternalUserDeleteConflictsJson.WithoutJobsToReplace
    | typeof InternalUserDeleteConflictsJson.WithoutJobsToReplaceLeadsOnly
    | typeof InternalUserDeleteConflictsJson.WithoutJobsToReplaceAllButLeads
    | typeof InternalUserDeleteConflictsJson.WithoutJobsToReplaceNoUsers;

type WarrantyServiceJobConflictJsonType =
    (typeof InternalUserDeleteConflictsJson.WithoutJobsToReplace.warrantyServiceJobs)[0];

type WarrantyServiceConflictJsonType = WarrantyServiceJobConflictJsonType["warrantyServices"][0];
type WarrantyServiceStatusJsonType = WarrantyServiceConflictJsonType["serviceStatus"];

export class IInternalUserDeleteConflictsFormValues {
    oldUserId: number;
    assignJobToId: number | null;

    newRfiAssignedUserId: number | null;
    newLeadAssignedUserId: number | null;
    newLeadProposalAssignedUserId: number | null;
    newLeadActivityAssignedUserId: number | null;
    newLeadContactFormDefaultUserId: number | null;
    newAccountingUserId: number | null;
    purchaseOrderJobs: EntityJobAssignment[] | null;
    warrantyServiceJobs: EntityJobAssignment[] | null;
    documentJobs: DocumentJobAssignment[] | null;
    newBillAssignedUserId: number | null;
}

export class EntityJobAssignment {
    constructor(jobId: number | null, loginId: number, userDisplayName: string) {
        this.jobId = jobId;
        this.newUserId = loginId;
        this.userDisplayName = userDisplayName;
    }

    jobId: number | null;
    newUserId: number;
    userDisplayName: string;
}

export class DocumentJobAssignment {
    constructor(jobId: number, loginId: number, userDisplayName: string) {
        this.jobId = jobId;
        this.newUserId = loginId;
        this.userDisplayName = userDisplayName;
    }

    jobId: number;
    newUserId: number;
    userDisplayName: string;
}

export class InternalUserDeleteConflictsEntity {
    constructor(data: InternalUserDeleteConflictsJsonType) {
        this.jobsites = data.jobsites?.map((job: any) => new JobsiteConflict(job)) ?? [];

        const defaultSelectItem = new BTSelectItem({ title: "-- NONE --", value: -1 });
        this.users = data.users?.map((user: any) => new User(user)) ?? [];
        this.usersWithJobViewing = [
            defaultSelectItem,
            ...this.users.filter((user) => user.canViewJobs).map((user) => user.toBTSelectItem()),
        ];
        this.usersWithLeadViewing = [
            defaultSelectItem,
            ...this.users.filter((user) => user.canViewLeads).map((user) => user.toBTSelectItem()),
        ];
        this.usersWithRfiViewing = [
            defaultSelectItem,
            ...this.users.filter((user) => user.canViewRfis).map((user) => user.toBTSelectItem()),
        ];
        this.usersWithAdminUpdatePermission = [
            defaultSelectItem,
            ...this.users
                .filter((user) => user.canUpdateAdmin)
                .map((user) => user.toBTSelectItem()),
        ];
        this.usersThatCanReassignSalesperson = [
            defaultSelectItem,
            ...this.users
                .filter((user) => user.canReassignSalesperson)
                .map((user) => user.toBTSelectItem()),
        ];
        this.usersWithBillViewing = [
            defaultSelectItem,
            ...this.users.filter((user) => user.canViewBills).map((user) => user.toBTSelectItem()),
        ];
        this.usersWithSchedulesViewing = [
            defaultSelectItem,
            ...this.users
                .filter((user) => user.canViewSchedules)
                .map((user) => user.toBTSelectItem()),
        ];

        this.purchaseOrderJobs =
            data.purchaseOrderJobs?.map(
                (poj: any) => new PurchaseOrderJobConflict(poj, this.users),
            ) ?? [];
        this.warrantyServiceJobs =
            data.warrantyServiceJobs?.map(
                (woj) => new WarrantyServiceJobConflict(woj, this.users),
            ) ?? [];
        this.documentJobs =
            data.documentJobs?.map(
                (doj: any) =>
                    new DocumentJobConflict(
                        doj,
                        this.users.filter((user) => user.canViewDocuments),
                    ),
            ) ?? [];
        this.leads = data.leads?.map((lead: any) => new LeadConflict(lead)) ?? [];
        this.leadActivities =
            data.leadActivities?.map((activity: any) => new LeadActivityConflict(activity)) ?? [];
        this.leadProposalTemplates =
            data.leadProposalTemplates?.map(
                (propTemp: any) => new LeadProposalTemplateConflict(propTemp),
            ) ?? [];
        this.rfis = data.rfis?.map((rfi: any) => new RfiConflict(rfi)) ?? [];
        this.bills = data.bills?.map((bill: any) => new BillConflict(bill)) ?? [];
        this.isContactFormDefaultUser = data.isContactFormDefaultUser;
        this.isAccountingUser = data.isAccountingUser;
        this.hasConflicts = data.hasConflicts;
    }

    jobsites: JobsiteConflict[];

    users: User[];
    usersWithJobViewing: BTSelectItem[];
    usersWithLeadViewing: BTSelectItem[];
    usersWithRfiViewing: BTSelectItem[];
    usersWithAdminUpdatePermission: BTSelectItem[];
    usersThatCanReassignSalesperson: BTSelectItem[];
    usersWithBillViewing: BTSelectItem[];
    usersWithSchedulesViewing: BTSelectItem[];

    purchaseOrderJobs: PurchaseOrderJobConflict[];
    warrantyServiceJobs: WarrantyServiceJobConflict[];
    documentJobs: DocumentJobConflict[];
    leads: LeadConflict[];
    leadActivities: LeadActivityConflict[];
    leadProposalTemplates: LeadProposalTemplateConflict[];
    rfis: RfiConflict[];
    bills: BillConflict[];

    isContactFormDefaultUser: boolean;
    isAccountingUser: boolean;
    hasConflicts: boolean;
}

export class User {
    constructor(data: any) {
        this.loginId = data.loginId;
        this.name = data.name;
        this.canViewJobs = data.canViewJobs;
        this.canViewLeads = data.canViewLeads;
        this.canReassignSalesperson = data.canReassignSalesperson;
        this.canUpdateAdmin = data.canUpdateAdmin;
        this.canViewRfis = data.canViewRfis;
        this.canViewDocuments = data.canViewDocuments;
        this.canViewBills = data.canViewBills;
        this.canViewSchedules = data.canViewSchedules;
    }

    loginId: number;
    name: string;
    canViewJobs: boolean;
    canViewLeads: boolean;
    canReassignSalesperson: boolean;
    canUpdateAdmin: boolean;
    canViewRfis: boolean;
    canViewDocuments: boolean;
    canViewBills: boolean;
    canViewSchedules: boolean;

    toBTSelectItem = () => new BTSelectItem({ name: this.name, value: this.loginId });
}

export class JobsiteConflict {
    constructor(data: any) {
        this.jobId = data.jobId;
        this.jobName = data.jobName;
        this.totalOtherUsers = data.totalOtherUsers;
    }

    jobId: number;
    jobName: string;
    totalOtherUsers: number;
}

export class RfiConflict {
    constructor(data: any) {
        this.rfiId = data.rfiId;
        this.titlePrefix = data.titlePrefix;
        this.title = data.title;
        this.status = data.status;
        this.assignedName = data.assignedName;
        this.jobPrefix = data.jobPrefix;
        this.dueDate = moment(data.dueDate);
    }

    rfiId: number;
    titlePrefix: string;
    title: string;
    status: RfiStatus;
    assignedName: string;
    jobPrefix: string;
    dueDate: moment.Moment;
}

export class LeadProposalTemplateConflict {
    constructor(data: any) {
        this.proposalId = data.proposalId;
        this.title = data.title;
        this.addedByDate = moment(data.addedByDate);
    }

    proposalId: number;
    title: string;
    addedByDate: moment.Moment;
}

export class LeadActivityConflict {
    constructor(data: any) {
        this.activityId = data.activityId;
        this.activityType = data.activityType;
        this.scheduledDate = moment(data.scheduledDate);
        this.opportunityTitle = data.opportunityTitle;
    }

    activityId: number;
    activityType: string;
    scheduledDate: moment.Moment;
    opportunityTitle: string;
}

export class LeadConflict {
    constructor(data: any) {
        this.leadId = data.leadId;
        this.opportunityTitle = data.opportunityTitle;
        this.dateAdded = moment(data.dateAdded);
    }

    leadId: number;
    opportunityTitle: string;
    dateAdded: moment.Moment;
}

export class PurchaseOrderJobConflict {
    constructor(data: any, users: User[]) {
        this.jobId = data.jobId;
        this.jobName = data.jobName;
        this.purchaseOrders = data.purchaseOrders.map((po: any) => new PurchaseOrderConflict(po));

        const associatedLoginIdLookup = new Set(data.associatedLoginIds);
        this.associatedLogins = [
            new BTSelectItem({ title: "-- NONE --", value: -1 }),
            ...users
                .filter((user) => associatedLoginIdLookup.has(user.loginId))
                .map((user) => user.toBTSelectItem()),
        ];
    }

    jobId: number;
    jobName: string;
    purchaseOrders: PurchaseOrderConflict[];
    associatedLogins: BTSelectItem[];
}

export class PurchaseOrderConflict {
    constructor(data: any) {
        this.purchaseOrderId = data.purchaseOrderId;
        this.ownerName = data.ownerName;
        this.workStatus = data.workStatus;
        this.dateAdded = moment(data.dateAdded);
        this.title = data.title;
    }

    purchaseOrderId: number;
    ownerName: string;
    workStatus: POVerifyWorkStatuses;
    dateAdded: moment.Moment;
    title: string;
}

export class BillConflict {
    constructor(data: any) {
        this.billId = data.billId;
        this.title = data.title;
        this.paymentStatus = data.paymentStatus;
        this.isSubRequested = data.isSubRequested;
        this.dateAdded = moment(data.dateAdded);
    }

    billId: number;
    isSubRequested: boolean;
    paymentStatus: POPaymentStatus;
    dateAdded: moment.Moment;
    title: string;
}

export class WarrantyServiceJobConflict {
    constructor(data: WarrantyServiceJobConflictJsonType, users: User[]) {
        this.jobId = data.jobId;
        this.jobName = data.jobName;
        this.warrantyServices = data?.warrantyServices.map((ws) => new WarrantyServiceConflict(ws));

        const associatedLoginIdLookup = new Set(data.associatedLoginIds);
        this.associatedLogins = [
            new BTSelectItem({ title: "-- NONE --", value: -1 }),
            ...users
                .filter((user) => associatedLoginIdLookup.has(user.loginId))
                .map((user) => user.toBTSelectItem()),
        ];
    }

    jobId: number;
    jobName: string;
    warrantyServices: WarrantyServiceConflict[];
    associatedLogins: BTSelectItem[];
}

export class WarrantyServiceConflict {
    constructor(data: WarrantyServiceConflictJsonType) {
        this.warrantyServiceId = data.warrantyServiceId;
        this.warrantyTitle = data.warrantyTitle;
        this.serviceStatus = new WarrantyServiceStatus(data.serviceStatus);
        this.warrantyClassification = data.warrantyClassification;
        this.appointmentDate = moment(data.appointmentDate);
    }

    warrantyServiceId: number;
    warrantyTitle: string;
    serviceStatus: WarrantyServiceStatus;
    warrantyClassification: string;
    appointmentDate?: moment.Moment;
}

export class WarrantyServiceStatus {
    constructor(data: WarrantyServiceStatusJsonType) {
        this.subAckStatusAsEnum = data.subAckStatusAsEnum;
        this.hoursUntilAppointmentDate = data.hoursUntilAppointmentDate;
        this.performedByLoginType = data.performedByLoginType;
        this.subNeedsToSetAppointment = data.subNeedsToSetAppointment;
        this.performingUserAcceptedDate = data.performingUserAcceptedDate
            ? moment(data.performingUserAcceptedDate)
            : undefined;
    }
    subAckStatusAsEnum: HoSubAck;
    hoursUntilAppointmentDate: number;
    performedByLoginType: BTLoginTypes;
    subNeedsToSetAppointment: boolean;
    performingUserAcceptedDate?: moment.Moment;
}

export class DocumentJobConflict {
    constructor(data: any, users: User[]) {
        this.jobId = data.jobId;
        this.jobName = data.jobName;
        this.documents = data.documents.map((doc: any) => new DocumentConflict(doc));

        const associatedLoginIdLookup = new Set(data.associatedLoginIds);
        this.associatedLogins = [
            new BTSelectItem({ title: "-- NONE --", value: -1 }),
            ...users
                .filter((user) => associatedLoginIdLookup.has(user.loginId))
                .map((user) => user.toBTSelectItem()),
        ];
    }

    jobId: number;
    jobName: string;
    documents: DocumentConflict[];
    associatedLogins: BTSelectItem[];
}

export class DocumentConflict {
    constructor(data: any) {
        this.documentInstanceId = data.documentInstanceId;
        this.title = data.title;
        this.folderName = data.folderName;
    }

    documentInstanceId: number;
    title: string;
    folderName: string;
}

export interface IInternalUserJobReassignRequest {
    oldLoginId: number;
    newLoginId: number;
}
